// Omnix/client/ui/SoftphoneController.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class SoftphoneController extends ComponentBase {
    static styles = `
        .softphone-controller-btn {
            position: relative;
        }
        .softphone-controller-status {
            position: absolute;
            top: 2px;
            right: 2px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: 2px solid var(--bs-body-bg);
        }
        .softphone-controller-status.connected {
            background-color: var(--bs-success);
        }
        .softphone-controller-status.disconnected {
            background-color: var(--bs-danger);
        }
        .softphone-controller-status.connecting {
            background-color: var(--bs-warning);
            animation: pulse 2s infinite;
        }
        @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
        }
    `;

    constructor(config) {
        super(config);
        this.softphoneInstance = null;
        this.credentials = null;
        this.hasCredentials = false;
        this.isConnected = false;
        this.isSoftphoneUIVisible = false; // Tracks UI visibility
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'softphone-controller-container';
        this.container.style.display = 'none';
        await super.initialize();
    }

    async initAfterLogin() {
        try {
            this.credentials = await this.appClient.rpcClient.call('OmniContactsRpc.getSipCredentials');

            if (this.credentials) {
                this.hasCredentials = true;
                this.container.style.display = 'block';
                this.log.info("SIP credentials loaded. Initializing softphone in the background.");

                if (!this.softphoneInstance) {
                    this.softphoneInstance = await this.createManagedComponent('Softphone', document.body, {
                        instanceId: 'global-softphone',
                        hideMinimizeButton: true,
                        hideCloseButton: true,
                        username: this.credentials.username
                    });
                }

                this.softphoneInstance.connect(
                    this.credentials,
                    {
                        registered: () => this.updateStatusIndicator('connected'),
                        unregistered: () => this.updateStatusIndicator('disconnected'),
                        registering: () => this.updateStatusIndicator('connecting'),
                        call_received: () => {
                            if (this.softphoneInstance) {
                                this.isSoftphoneUIVisible = true;
                                this.softphoneInstance.show();
                            }
                        },
                        shown: () => { this.isSoftphoneUIVisible = true; },
                        hidden: () => { this.isSoftphoneUIVisible = false; },
                        destroyed: () => {
                            this.isSoftphoneUIVisible = false;
                            this.softphoneInstance = null;
                            this.updateStatusIndicator('disconnected');
                        }
                    }
                );
            } else {
                this.hasCredentials = false;
                this.container.style.display = 'none';
                this.log.info("No SIP credentials found for the current user. Softphone is disabled.");
            }
        } catch (error) {
            this.hasCredentials = false;
            this.container.style.display = 'none';
            this.log.error("Failed to fetch SIP credentials due to a server error:", error);
        }
        this.render();
    }

    toggleSoftphoneUI() {
        if (!this.softphoneInstance) {
            if (this.hasCredentials) {
                this.log.warn("Softphone instance not found, re-initializing on toggle.");
                this.initAfterLogin();
            } else {
                this.appClient.notifier.warn("You do not have a phone extension assigned.");
            }
            return;
        }
        if (this.isSoftphoneUIVisible) {
            this.softphoneInstance.hide();
        } else {
            this.softphoneInstance.show();
        }
    }

    render() {
        this.container.innerHTML = `
            <button class="btn btn-sm btn-outline-secondary softphone-controller-btn" title="Toggle Phone UI" ${!this.hasCredentials ? 'disabled' : ''}>
                <i class="bi bi-telephone-fill"></i>
                <span class="softphone-controller-status disconnected"></span>
            </button>
        `;
        this.container.querySelector('button').addEventListener('click', () => this.toggleSoftphoneUI());
        this.updateStatusIndicator(this.isConnected ? 'connected' : 'disconnected');
        return this.container;
    }


    updateStatusIndicator(status) {
        this.isConnected = (status === 'connected');
        const statusIndicator = this.container.querySelector('.softphone-controller-status');
        if (statusIndicator) {
            statusIndicator.className = `softphone-controller-status ${status}`;
        }
    }

    destroy() {
        this.softphoneInstance?.destroy();
        super.destroy();
    }
}